import axios from 'axios';
import { toast } from 'react-toastify';
import { MockHandler } from '@/utils/mockHandler';
import { showNotification } from '@/utils/notifications';
import type { CacheEntry, PendingRequest, ApiResponse, PaginationParams } from '@/types/api';
import { isPublicRoute } from '@/config/navigation';

// Use dev API for testing
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://dev-api.skillsture.io/api';
const APP_ENV = import.meta.env.VITE_APP_ENV || import.meta.env.APP_ENV || 'production';
const isLocalEnv = APP_ENV === 'local';
const isDevEnv = APP_ENV === 'dev';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL
const DEDUPE_WINDOW = 2000; // 2 seconds deduplication window

const responseCache = new Map<string, CacheEntry<any>>();
const pendingRequests = new Map<string, PendingRequest>();

// Helper function to generate cache key
const generateCacheKey = (endpoint: string, method: string, params?: any, data?: any): string => {
  const keyParts = [
    endpoint,
    method,
    params ? JSON.stringify(params) : '',
    data ? JSON.stringify(data) : ''
  ];
  return btoa(JSON.stringify(keyParts)); // Base64 encode to avoid long keys
};

// Cache cleanup function (runs every minute)
setInterval(() => {
  const now = Date.now();
  
  // Clean up expired cache entries
  responseCache.forEach((entry, key) => {
    if (now > entry.expiry) {
      responseCache.delete(key);
    }
  });
  
  // Clean up old pending requests
  pendingRequests.forEach((request, key) => {
    if (now - request.timestamp > DEDUPE_WINDOW) {
      pendingRequests.delete(key);
    }
  });
}, 60000);


const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Enhanced error handling with user-friendly messages
const getFriendlyErrorMessage = (error: any): string => {
  const status = error.response?.status;
  const backendMessage = error.response?.data?.message || error.response?.data?.error;
  
  // If we have a specific backend message, use it
  if (backendMessage) {
    return backendMessage;
  }
  
  // Provide user-friendly messages based on status codes
  switch (status) {
    case 400:
      return 'The request was invalid. Please check your input and try again.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 408:
    case 504:
      return 'The request timed out. Please try again.';
    case 409:
      return 'This action conflicts with existing data. Please check and try again.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'An internal server error occurred. Our team has been notified.';
    case 502:
    case 503:
      return 'Service temporarily unavailable. Please try again shortly.';
    default:
      if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
        return 'Unable to connect to the server. Please check your internet connection.';
      }
      return 'An unexpected error occurred. Please try again.';
  }
};

// Request interceptor - proactively refresh tokens before requests
api.interceptors.request.use(async (config: any) => {
  const token = localStorage.getItem('auth_token');
  const tokenExpiry = localStorage.getItem('token_expiry');
  const refreshToken = localStorage.getItem('refresh_token');
  
  // Check if token is expired or about to expire before making request
  if (token && tokenExpiry) {
    const expiryTime = parseInt(tokenExpiry);
    const now = Date.now();
    // Buffer: 5 minutes for production (30 min tokens)
    // Adjust based on your token TTL - should be ~20% of token lifetime
    const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes buffer for production (30 min tokens)
    const isExpired = now >= expiryTime;
    const isExpiringSoon = now >= (expiryTime - TOKEN_REFRESH_BUFFER);
    
    // Proactively refresh token if expired or expiring soon, and we have a refresh token
    if ((isExpired || isExpiringSoon) && refreshToken) {
      // Skip refresh for auth endpoints to prevent loops
      const isAuthEndpoint = config.url?.includes('/auth/') || 
                            config.url?.includes('/login') ||
                            config.url?.includes('/logout') ||
                            config.url?.includes('/refresh');
      
      if (!isAuthEndpoint) {
        try {
          console.log(`Token ${isExpired ? 'expired' : 'expiring soon'}, proactively refreshing before request...`);
          // Import authApi dynamically to avoid circular dependency
          const { authApi } = await import('./authApi');
          await authApi.refreshToken();
          
          // Update token reference after refresh
          const newToken = localStorage.getItem('auth_token');
          if (newToken && config.headers) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
          
          console.log('Token refreshed successfully before request');
          return config;
        } catch (refreshError: any) {
          console.error('Failed to refresh token proactively:', refreshError);
          // If refresh fails, let the request proceed - response interceptor will handle 401
          // Don't throw here to allow the request to go through and get proper error handling
        }
      }
    }
  }
  
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // If data is FormData, remove Content-Type header to let axios set it with boundary
  if (config.data instanceof FormData && config.headers) {
    delete config.headers['Content-Type'];
    
    if (isDevEnv || isLocalEnv) {
      // Debug FormData
      console.log('Sending FormData:', {
        url: config.url,
        method: config.method?.toUpperCase(),
        hasAvatar: config.data.has('avatar'),
        formDataEntries: Array.from(config.data.entries() as Iterable<[string, any]>).map(([key, value]) => ({
          key,
          value: value instanceof File ? {
            name: value.name,
            size: value.size,
            type: value.type
          } : value
        }))
      });
    }
  }
  
  // Log all requests for debugging
  if (isDevEnv || isLocalEnv) {
    console.log('API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      hasToken: !!token,
      contentType: config.headers?.['Content-Type'],
      isFormData: config.data instanceof FormData,
      dataType: typeof config.data,
    });
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    if (isDevEnv || isLocalEnv) {
      console.log('API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
    }
    return response;
  },
  (error) => {
      
    const errorMessage = error.response?.error || error.response?.message || 'Failed response...';
    
    if (isDevEnv || isLocalEnv) {
      console.error('API Error:', {
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
        message: error.message,
        responseData: error.response?.data
      });
    }

    if (error.response?.status === 401) {
      // Handle unauthorized
      const currentPath = window.location.pathname;
      const refreshToken = localStorage.getItem('refresh_token');
      
      // Check if we just logged in - don't clear tokens if so (might be timing issue)
      const lastLoginTime = localStorage.getItem('last_login_time');
      const justLoggedIn = lastLoginTime && (Date.now() - parseInt(lastLoginTime)) < 10000; // 10 seconds
      
      if (justLoggedIn) {
        console.warn('401 error right after login, keeping tokens (might be timing issue)', {
          url: error.config?.url,
          path: currentPath
        });
        // Don't clear tokens or redirect - might be a timing issue
        return Promise.reject(error);
      }
      
      // Don't redirect if we're on a public route
      if (isPublicRoute(currentPath)) {
        return Promise.reject(error);
      }
      
      // Check if this is an auth endpoint (login, logout, refresh)
      const isAuthEndpoint = error.config?.url?.includes('/auth/') || 
                            error.config?.url?.includes('/login') ||
                            error.config?.url?.includes('/logout') ||
                            error.config?.url?.includes('/refresh');
      
      // Special handling for refresh endpoint - don't clear tokens here, let authApi handle it
      const isRefreshEndpoint = error.config?.url?.includes('/auth/refresh') || 
                                error.config?.url?.includes('/refresh');
      
      // If we have a refresh token and it's NOT an auth endpoint,
      // don't redirect immediately - let UserContext handle token refresh
      // This allows token refresh to happen before redirecting to login
      if (refreshToken && !isAuthEndpoint) {
        // Check if we're already on login page to prevent redirect loops
        if (currentPath !== '/login') {
          console.warn('Unauthorized request (401), but refresh token exists. Allowing UserContext to handle token refresh.');
          // Don't clear tokens or redirect - let UserContext try to refresh
          // The error will propagate and UserContext will handle it
          return Promise.reject(error);
        }
      }
      
      // For refresh endpoint failures, don't clear tokens here - authApi.ts handles it
      if (isRefreshEndpoint) {
        console.warn('Refresh token endpoint failed, letting authApi handle token cleanup');
        return Promise.reject(error);
      }
      
      // No refresh token, auth endpoint failed (except refresh), or already on login page
      // Clear tokens and redirect to login
      console.warn('Clearing tokens due to 401 error', {
        url: error.config?.url,
        hasRefreshToken: !!refreshToken,
        isAuthEndpoint,
        currentPath
      });
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      localStorage.removeItem('refresh_token_timestamp');
      localStorage.removeItem('last_refresh_attempt');
      if (currentPath !== '/login') {
        window.location.href = '/login';
        console.warn('Unauthorized access - redirecting to login', errorMessage);
      }
    }
    
    const friendlyMessage = getFriendlyErrorMessage(error);
    
    // Show notification for network errors only (not for auth, validation, etc.)
    if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
      showNotification('error', friendlyMessage);
    }
      
    // Always show toast with the friendly message
    // toast.error(friendlyMessage, {
    //   position: "top-right",
    //   autoClose: 5000,
    //   hideProgressBar: false,
    //   closeOnClick: true,
    //   pauseOnHover: true,
    //   draggable: true,
    // });
    
    return Promise.reject(error);
  }
);

// Enhanced API call wrapper with mock support
export const apiCall = async <T>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    data?: any;
    params?: any;
    mockData: T;
    mockDelay?: number;
  }
): Promise<ApiResponse<T>> => {
  const { method = 'GET', data, params, mockData, mockDelay = 500 } = options;

  // Bypass mode - directly return mock data
  if (MockHandler.getMockMode() === 'bypass') {
    await new Promise(resolve => setTimeout(resolve, mockDelay));
    return {
      data: mockData,
      status: 200,
      success: true,
      message: 'Mock data (bypass mode)'
    };
  }

  try {
    const response = await api({
      url: endpoint,
      method,
      data,
      params
    });

    const apiData = response.data;

    // API mode - check if we need to use mock data as fallback
    if (MockHandler.shouldUseMock(true, apiData)) {
      MockHandler.handleEmptyResponse(endpoint);
      return {
        data: mockData,
        status: response.status,
        success: true,
        message: 'Mock data (fallback)'
      };
    }

    return {
      data: apiData,
      status: response.status,
      success: true
    };
  } catch (error: any) {
    if (isDevEnv || isLocalEnv) {
      console.error(`API call failed for ${endpoint}:`, error);
    }

    // API mode - use mock data on error
    if (MockHandler.getMockMode() === 'api') {
      showNotification('info', `Using mock data for ${endpoint} due to API error`);
      return {
        data: mockData,
        status: error.response?.status || 500,
        success: false,
        message: 'Mock data (error fallback)'
      };
    }

    // No mock mode - rethrow error
    throw error;
  }
};

// Enhanced API call wrapper with caching and deduplication
export const cachedApiCall = async <T>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    data?: any;
    params?: any;
    mockData: T;
    mockDelay?: number;
    useCache?: boolean; // Enable/disable caching per call
    cacheTTL?: number; // Custom TTL for this call
  }
): Promise<ApiResponse<T>> => {
  const { 
    method = 'GET', 
    data, 
    params, 
    mockData, 
    mockDelay = 500,
    useCache = true,
    cacheTTL = CACHE_TTL
  } = options;

  // Generate cache key for GET requests (we typically only cache GET)
  const cacheKey = generateCacheKey(endpoint, method, params, data);
  
  // Check cache first for GET requests
  if (useCache && method === 'GET') {
    const cached = responseCache.get(cacheKey);
    if (cached && Date.now() < cached.expiry) {
      if (isDevEnv || isLocalEnv) {
        console.log(`[Cache] Serving cached response for: ${endpoint}`);
      }
      return cached.data;
    }
  }

  // Check for duplicate in-flight requests
  if (pendingRequests.has(cacheKey)) {
    const pending = pendingRequests.get(cacheKey);
    // Only dedupe if request is recent (within dedupe window)
    if (pending && Date.now() - pending.timestamp < DEDUPE_WINDOW) {      
      if (isDevEnv || isLocalEnv) {
        console.log(`[Dedupe] Reusing in-flight request for: ${endpoint}`);
      }
      return pending.promise;
    }
  }

  // Create the actual API call promise
  const apiPromise = (async (): Promise<ApiResponse<T>> => {
    try {
      // Bypass mode - directly return mock data
      if (MockHandler.getMockMode() === 'bypass') {
        await new Promise(resolve => setTimeout(resolve, mockDelay));
        const result = {
          data: mockData,
          status: 200,
          success: true,
          message: 'Mock data (bypass mode)'
        };
        
        // Cache the mock response if it's a GET request
        if (useCache && method === 'GET') {
          responseCache.set(cacheKey, {
            data: result,
            timestamp: Date.now(),
            expiry: Date.now() + cacheTTL
          });
        }
        
        return result;
      }

      const response = await api({
        url: endpoint,
        method,
        data,
        params
      });

      const apiData = response.data;

      // API mode - check if we need to use mock data as fallback
      if (MockHandler.shouldUseMock(true, apiData)) {
        MockHandler.handleEmptyResponse(endpoint);
        const result = {
          data: mockData,
          status: response.status,
          success: true,
          message: 'Mock data (fallback)'
        };
        
        // Cache the mock fallback if it's a GET request
        if (useCache && method === 'GET') {
          responseCache.set(cacheKey, {
            data: result,
            timestamp: Date.now(),
            expiry: Date.now() + cacheTTL
          });
        }
        
        return result;
      }

      const result = {
        data: apiData,
        status: response.status,
        success: true
      };

      // Cache successful GET responses
      if (useCache && method === 'GET') {
        responseCache.set(cacheKey, {
          data: result,
          timestamp: Date.now(),
          expiry: Date.now() + cacheTTL
        });
      }

      return result;

    } catch (error: any) {      
      if (isDevEnv || isLocalEnv) {
        console.error(`API call failed for ${endpoint}:`, error);
      }

      // API mode - use mock data on error
      if (MockHandler.getMockMode() === 'api') {
        showNotification('info', `Using mock data for ${endpoint} due to API error`);
        const result = {
          data: mockData,
          status: error.response?.status || 500,
          success: false,
          message: 'Mock data (error fallback)'
        };
        
        // Even cache error fallbacks for GET to prevent retries
        if (useCache && method === 'GET') {
          responseCache.set(cacheKey, {
            data: result,
            timestamp: Date.now(),
            expiry: Date.now() + (cacheTTL / 2) // Shorter TTL for error responses
          });
        }
        
        return result;
      }

      throw error;
    } finally {
      // Remove from pending requests
      pendingRequests.delete(cacheKey);
    }
  })();

  // Store the promise for deduplication
  if (useCache) {
    pendingRequests.set(cacheKey, {
      promise: apiPromise,
      timestamp: Date.now()
    });
  }

  return apiPromise;
};

// Clear cache for specific endpoints (useful for after mutations)
export const clearApiCache = (endpointPattern?: string | RegExp) => {
  if (!endpointPattern) {
    responseCache.clear();    
    if (isDevEnv || isLocalEnv) {
      console.log('[Cache] Cleared all API cache');
    }
    return;
  }

  const pattern = typeof endpointPattern === 'string' 
    ? new RegExp(endpointPattern)
    : endpointPattern;

  let clearedCount = 0;
  responseCache.forEach((_, key) => {
    const decodedKey = atob(key);
    if (pattern.test(decodedKey)) {
      responseCache.delete(key);
      clearedCount++;
    }
  });
  
  if (isDevEnv || isLocalEnv) {
    console.log(`[Cache] Cleared ${clearedCount} cache entries for pattern: ${endpointPattern}`);
  }
};

// Professional API
export const professionalApi = {
  // Get all professionals with filters
  getProfessionals: (params?: {
    category_id?: number;
    available_for_events?: boolean;
    available_for_mentoring?: boolean;
    search?: string;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    per_page?: number;
  }) => api.get('/professionals', { params }),

  // Get single professional
  getProfessional: (id: number) => api.get(`/professionals/${id}`),

  // Get professional's upcoming events
  getUpcomingEvents: (id: number) => api.get(`/professionals/${id}/upcoming-events`),

  // Check availability
  checkAvailability: (id: number, eventDate: string) => 
    api.get(`/professionals/${id}/availability`, { params: { event_date: eventDate } }),

  // Public professionals (without auth)
  getPublicProfessionals: (featured?: boolean) => 
    api.get('/public/professionals', { params: { featured } }),
};

// Add this to clear cache after mutations
export const clearProfessionalCache = () => {
  clearApiCache('/professionals');
};

// Professional Category API
export const categoryApi = {
  getCategories: () => api.get('/professional-categories'),
  getCategory: (id: number) => api.get(`/professional-categories/${id}`),
};

// Professional Rating API
export const ratingApi = {
  getRatings: (professionalId: number, params?: { featured?: boolean; page?: number }) =>
    api.get(`/professionals/${professionalId}/ratings`, { params }),

  createRating: (professionalId: number, data: {
    rating: number;
    review?: string;
    rating_breakdown?: Record<string, number>;
    event_id?: number;
    networking_session_id?: number;
  }) => api.post(`/professionals/${professionalId}/ratings`, data),

  updateRating: (ratingId: number, data: {
    rating?: number;
    review?: string;
    rating_breakdown?: Record<string, number>;
  }) => api.put(`/ratings/${ratingId}`, data),

  deleteRating: (ratingId: number) => api.delete(`/ratings/${ratingId}`),
};

// Event API
export const eventApi = {
  getEvents: (params?: {
    type?: string;
    status?: string;
    page?: number;
    per_page?: number;
  }) => api.get('/v1/events', { params }),

  getEvent: (id: string | number) => api.get(`/v1/events/${id}`),

  registerForSchedule: (scheduleId: string | number) => api.post(`/v1/events/schedules/${scheduleId}/register`),

  cancelRegistration: (scheduleId: string | number) => api.delete(`/v1/events/schedules/${scheduleId}/register`),

  checkRegistrationStatus: (scheduleId: string | number) => api.get(`/v1/events/schedules/${scheduleId}/check-registration`),

  getMyRegistrations: (params?: { upcoming?: boolean; page?: number }) => api.get('/v1/events/my/registrations', { params }),

  getMyQuotas: () => api.get('/v1/events/my/quotas'),
};

// Skillstree API
export const skillstreeApi = {
  getSkillstreeById: (id: string | number) => api.get(`/v1/skillstrees/${id}`),
  getCoreCategoryWithSkills: (slug: string) => api.get(`/v1/core-category/${slug}`),
  getCoreCategories: () => api.get('/v1/core-category'),
  getSkillstrees: (params?: {
    coreCategorySlug?: string;
    skillstreeSlug?: string;
    keywords?: string;
    isEnrolled?: boolean;
  }) => api.get('/v1/skillstrees', { params }),
  enroll: (skillstreeId: number) => api.post('/v1/skillstrees/enroll', { skillstree_id: skillstreeId }),
};

// Microskill API
export const microskillApi = {
  getMicroskillById: (id: string | number) => api.get(`/v1/microskills/${id}`),
};

// Video API
export const videoApi = {
  getVideoById: (id: string | number) => api.get(`/v1/videos/${id}`),
  // Get user's progress for a specific video
  getVideoProgress: (videoResourceId: string | number) => api.get(`/v1/videos/${videoResourceId}/progress`),
  // Update video watch progress
  updateVideoProgress: (data: {
    video_resource_id: number;
    completion_rate: number;
    last_stopped_at?: string;
  }) => api.post('/v1/videos/progress', data),
};

export const getPublicProfile = async (username: string) => {
  try {
    const response:any = await api.get(`/profile/${username}`);
    // console.log('API response:', response);
    return { data: response.data };
  } catch (error) {    
    if (isDevEnv || isLocalEnv) {
      console.error('Error fetching public profile:', error);
    }
  }
};

export const getCredentialDetails = async (credentialId: string, type: string) => {
  try {    
    const response:any = await api.get(`/profile/${credentialId}/${type}`);
    // console.log('API response:', response);
    return { data: { ...response.data, type: type } };
  } catch (error) {    
    if (isDevEnv || isLocalEnv) {
      console.error('Error fetching credential details:', error);
    }
  }
};

// Profile API
export const profileApi = {
  getProfile: () => api.get('/profile'),
};

export default api;